/**
 * Passerelle entre l'exercice de la clôture annuelle (`ExerciceFiscal`, code = année) et le registre des
 * exercices de l'entreprise (écran Exercices). Fonctions pures : le stockage est branché par l'appelant.
 */
import type { ExerciceFiscal } from '@shared/types/ExerciceFiscal';
import { uuid } from '@shared/utils/uuid';

export interface ExerciceRegistre {
  id: string;
  annee: string;
  code?: string;
  commentaire?: string;
  date_debut: string;
  date_fin: string;
  statut: 'ouvert' | 'cloture';
}

const estPersiste = (e: ExerciceRegistre): boolean => !e.id.startsWith('derived-');

/** L'exercice de l'année est-il enregistré comme clôturé ? (une ligne seulement « observée » ne compte pas.) */
export function exerciceClotureDansRegistre(registre: readonly ExerciceRegistre[], annee: string): boolean {
  return registre.some((e) => e.annee === annee && estPersiste(e) && e.statut === 'cloture');
}

/** Registre après clôture de l'exercice (étape atomique de la clôture annuelle). */
export function enregistrerStatutExercice(
  registre: readonly ExerciceRegistre[],
  exercice: ExerciceFiscal,
  nouvelId: () => string = () => uuid('exo'),
): ExerciceRegistre[] {
  const annee = exercice.code;
  const existant = registre.find((e) => e.annee === annee && estPersiste(e));
  const ligne: ExerciceRegistre = {
    id: existant?.id ?? nouvelId(),
    annee,
    code: existant?.code ?? `EX-${annee}`,
    ...(existant?.commentaire ? { commentaire: existant.commentaire } : {}),
    date_debut: existant?.date_debut ?? exercice.date_debut,
    date_fin: existant?.date_fin ?? exercice.date_fin,
    statut: exercice.statut === 'cloture' || exercice.statut === 'archive' ? 'cloture' : 'ouvert',
  };
  return [...registre.filter((e) => e.annee !== annee), ligne];
}

/** Annule l'étape ci-dessus : remet la ligne d'avant, ou retire la ligne si l'année n'était pas enregistrée. */
export function restaurerStatutExercice(
  registre: readonly ExerciceRegistre[],
  annee: string,
  avant: ExerciceRegistre | undefined,
): ExerciceRegistre[] {
  const sans = registre.filter((e) => e.annee !== annee);
  return avant ? [...sans, avant] : sans;
}
